import React, { useEffect, useState } from 'react'

import { fontSize } from './themes/themes'
import AppLayout from './layout/AppLayout'
import { Toaster } from 'react-hot-toast'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router'
import { RENDER_PROTECTED_ROUTES, RENDER_UNPROTECTED_ROUTES } from './configs/routes'
import AuthLayout from './layout/AuthLayout'
import { useSelector } from 'react-redux'
import { currentUserSelector } from './stores/current-user/selector'
import { loginPath } from './constants/route-name'
import { AuthService } from './features/auth/services/AuthService'
import { useAppDispatch } from './stores/store'
import OnboardingPage from './pages/OnboardingPage'
import { resetRoleState } from './features/user/stores/role/slice'
import { resetUserState } from './features/user/stores/user/slice'
import { resetBannerState } from './features/content/stores/banner/slice'
import { resetPageState } from './features/content/stores/pageContent/slice'
import { resetPopupState } from './features/content/stores/popup/slice'
import { resetInsurerState } from './features/insurance/stores/insurer/slice'
import { Language } from './types/types'
import { resetLanguage } from './stores/language/slice'
import { resetPlanState } from './features/insurance/stores/plan/slice'
import { resetBenefitState } from './features/content/stores/benefit/slice'
import { resetProductState } from './features/content/stores/product/slice'
import { resetFAQState } from './features/content/stores/faq/slice'
import { resetCustomerState } from './features/customer/stores/slice'
import { resetDealerMasterState } from './features/dealer-master/stores/dealer-master/slice'
import { resetTransactionState } from './features/transaction/stores/slice'
import { resetPromotionState } from './features/promotion/stores/slice'
import { resetCommissionState } from './features/dealer-master/stores/commission/slice'

const toastOptions = {
  style: {
    fontSize: fontSize.h6,
    fontWeight: 500,
    borderRadius: '50px'
  }
}

function App() {
  const [isLoading, setIsLoading] = useState(true)

  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const currentUser = useSelector(currentUserSelector)

  const token = localStorage.getItem('token')
  const notFoundElement = currentUser?.name ? <Navigate to='/home' /> : <Navigate to={loginPath} />

  const onFetchUser = async () => {
    if (token) {
      try {
        setIsLoading(true)
        await dispatch(AuthService.getCurrentUserAsyncThunk()).unwrap()
      } catch (error) {
        localStorage.clear()
      }
    }
    setIsLoading(false)
  }

  useEffect(() => {
    localStorage.setItem('language', Language.TH)
    onFetchUser()
    if (location.pathname === '/') navigate(loginPath, { replace: true })
  }, [])

  useEffect(() => {
    // Ask user before navigate to OUTSIDE or CLOSE TAB
    window.onbeforeunload = function () {
      return location.pathname.includes('edit') || location.pathname.includes('add')
        ? 'Are you sure you want to leave?'
        : undefined
    }
    if (token) dispatch(AuthService.getCurrentUserAsyncThunk())
    dispatch(resetUserState())
    dispatch(resetRoleState())
    dispatch(resetBannerState())
    dispatch(resetPageState())
    dispatch(resetPopupState())
    dispatch(resetBenefitState())
    dispatch(resetProductState())
    dispatch(resetFAQState())
    dispatch(resetInsurerState())
    dispatch(resetCustomerState())
    dispatch(resetDealerMasterState())
    dispatch(resetPlanState())
    dispatch(resetTransactionState())
    dispatch(resetLanguage())
    dispatch(resetPromotionState())
    dispatch(resetCommissionState())
  }, [location.pathname])

  if (isLoading)
    return (
      <Routes>
        <Route path='*' element={<OnboardingPage />} />
      </Routes>
    )

  return (
    <>
      <Toaster toastOptions={toastOptions} />
      <Routes>
        <Route path='/' element={notFoundElement} />
        {currentUser?.name ? (
          <Route path='/' element={<AppLayout />}>
            <Route index element={<></>} />
            {RENDER_PROTECTED_ROUTES.map(
              (route) =>
                (currentUser.permissions.includes(route.permissions) || !route.permissions) && (
                  <Route key={route.path} path={route.path} element={route.component} />
                )
            )}
          </Route>
        ) : (
          <Route path='/' element={<AuthLayout />}>
            {RENDER_UNPROTECTED_ROUTES.map((route) => (
              <Route key={route.path} path={route.path} element={route.component} />
            ))}
          </Route>
        )}
        <Route path='*' element={notFoundElement} />
      </Routes>
    </>
  )
}

export default App
